import log, { METRIC_NAME } from "../logging";
import { getPortalConfig } from "../configurations";
import {
    getDeviceBrowser,
    getDeviceModel,
    getDeviceOSName,
    getDeviceTouchInfo,
    getDeviceType,
    isInternalTestTraffic,
} from "../utils/userAgentUtils";
import {
    fetchStreamingURLCache,
    refreshStreamingURLCache,
} from "../utils/StreamingURLCache";
import { useSessionLaunchMetricStore } from "../hooks/useSessionLaunchMetricStore";
import { CookieList } from "../types/cookies";
import { sendCreateRemoteBrowserCommand } from "../utils/ermineSsrClientUtil";
import { getBrowserLanguage, getBrowserTimeZone, getDeepLink } from "../utils";
import { isEmptyList } from "../utils/commonUtils";
import { getFormattedSdkErrorMessage } from "../utils/errorUtils";
import { useMutation } from "@tanstack/react-query";
import { ServiceException } from "@smithy/smithy-client";
import { CreateRemoteBrowserResponse } from "@amzn/ermine-server-side-rendering-client";

const publishDeviceMetrics = () => {
    log.publishMetricWithDimension(
        METRIC_NAME.DEVICE_BROWSER,
        getDeviceBrowser()
    );
    log.publishMetricWithDimension(METRIC_NAME.DEVICE_TYPE, getDeviceType());
    log.publishMetricWithDimension(METRIC_NAME.DEVICE_MODEL, getDeviceModel());
    log.publishMetricWithDimension(METRIC_NAME.DEVICE_OS, getDeviceOSName());
    log.publishMetricWithDimension(
        METRIC_NAME.DEVICE_TOUCHSCREEN,
        getDeviceTouchInfo()
    );
};

export const useGetStreamingUrl = () => {
    const setCachedSessionEndpointLoadedTimestampIfEmpty = useSessionLaunchMetricStore(
        (store) => store.setCachedSessionEndpointLoadedTimestampIfEmpty
    );
    const setSessionEndpointCreatedTimestampIfEmpty = useSessionLaunchMetricStore(
        (store) => store.setSessionEndpointCreatedTimestampIfEmpty
    );

    const mutationResult = useMutation<
        string,
        ServiceException & Error,
        CookieList
    >({
        mutationFn: async (cookies: CookieList): Promise<string> => {
            const cachedStreamingURL = fetchStreamingURLCache();

            if (cachedStreamingURL !== undefined) {
                const { streamingURL } = cachedStreamingURL;
                log.publishCounterMetric(METRIC_NAME.STREAMING_URL_CACHE_HIT);
                setCachedSessionEndpointLoadedTimestampIfEmpty();

                return streamingURL;
            }

            let data: CreateRemoteBrowserResponse;
            try {
                const portalConfig = getPortalConfig();
                data = await sendCreateRemoteBrowserCommand({
                    portalArn: portalConfig.portalArn,
                    sessionConfig: {
                        supportedLanguage: getBrowserLanguage(),
                        timeZone: getBrowserTimeZone(),
                        deepLink: getDeepLink(),
                    },
                    cookies: isEmptyList(cookies)
                        ? null
                        : JSON.stringify(cookies),
                });
            } catch (err) {
                await log.error(
                    err
                        ? getFormattedSdkErrorMessage(err, "OidcBrowserCreator")
                        : "Failed to create streaming endpoint"
                );
                throw err;
            }

            try {
                log.publishHttpStatusMetric(
                    METRIC_NAME.CREATE_REMOTE_BROWSER,
                    200
                );
                setSessionEndpointCreatedTimestampIfEmpty();

                if (!isInternalTestTraffic()) {
                    publishDeviceMetrics();
                }

                // Refill cache and log warning in case it failed
                const isCacheWriteSuccess = refreshStreamingURLCache(
                    data.streamingUrl
                );
                if (!isCacheWriteSuccess) {
                    await log.warn(
                        "StreamingURL session cache write back failed."
                    );
                }

                return data.streamingUrl;
            } catch (err) {
                await log.error("Failed to consume fresh streaming URL", err);
                throw err;
            }
        },
    });

    return {
        getStreamingUrl: mutationResult.mutateAsync,
        error: mutationResult.error,
        isLoading: mutationResult.isLoading,
    };
};
